<template>
  <div data-view="scheduled-threads">
    <layout-logged-frame title="Scheduled threads" has-before>
      <template v-slot:before>
        <NavigationTabs
          :tabs="navigationTabs"
          :tabs-items-counter="tabsItemsCounter"
        />
      </template>

      <template v-slot:content>
        <ScheduledThreadFilters
          @filter="onFilter"
          @clearFilters="onClearFilters"
        />

        <table-base
          :items="threads"
          :columns="columns"
          :pages="totalPages"
          :pagination="pagination"
          :title="`${pagination.totalItems || 0} threads`"
          :is-loading="isLoading"
          has-custom-items-template
          :on-select-item="selectThread"
          :on-change-page="onChangePage"
          :on-update-pagination="onUpdatePagination"
        >
          <template v-slot:item-cell="scope">
            <template v-if="scope.cell.column.value === 'release_date'">
              <PinDate :date="scope.cell.item.release_date" />
            </template>

            <template v-else-if="scope.cell.column.value === 'deal'">
              <ScheduledThread
                v-if="scope.cell.item.thread"
                :thread="scope.cell.item.thread"
              />
            </template>

            <template v-else-if="scope.cell.column.value === 'merchant'">
              <span
                v-if="scope.cell.item.thread.merchant"
                class="merchant-name"
              >
                {{ scope.cell.item.thread.merchant.name }}
              </span>
            </template>

            <template v-else-if="scope.cell.column.value === 'expired'">
              <v-icon v-if="scope.cell.item.expired" color="success">
                check
              </v-icon>
              <v-icon v-else color="error"> close </v-icon>
            </template>

            <template v-else>
              {{ displayColumnValue(scope.cell.item, scope.cell.column.value) }}
            </template>
          </template>
        </table-base>
      </template>
    </layout-logged-frame>
  </div>
</template>

<script>
  import ScheduledThreadFilters from '../../components/forms/filters/ScheduledThreads';
  import PinDate from '../../components/panels/Date';
  import ScheduledThread from '../../components/panels/schedules/Thread';
  import { isStr, extractNestedProp } from '@/utils';
  import columns from '../../config/scheduledThreads/threadsColumns';
  import navigationTabs from '../../config/scheduledThreads/navigationTabs';
  import NavigationTabs from '../../components/NavigationTabs'

  export default {
    components: {
      ScheduledThreadFilters,
      PinDate,
      ScheduledThread,
      NavigationTabs
    },
    data() {
      return {
        pagination: {
          page: 1,
          rowsPerPage: import.meta.env.VUE_APP_API_MAX_ITEMS_PER_PAGE,
          totalItems: 0,
          search: '',
          sortBy: 'start_date',
          order: 'asc' ,
        },
        navigationTabs,
        selectedThread: {},
        query: {},
        totalPages: 0,
        columns,
        isLoading: false,
      };
    },
    computed: {
      tabsItemsCounter() {
        if(this.$route.name === 'scheduled-threads-today') {
          return [null, this.pagination.totalItems];
        }
        return [this.pagination.totalItems];
      },
      threads() {
        return this.$store.getters['schedules/threads'];
      },
      threadsMeta() {
        return this.$store.getters['schedules/threadsMeta'];
      },
      totalCount() {
        return this.pagination.totalItems || 0;
      },
      route() {
        return this.$route.path;
      }
    },
    watch: {
      route() {
        this.getThreads(this.query);
      }
    },
    methods: {
      getThreads({ page, thread_id = -1, start_date = '', end_date = '', countries } = {}) {
        const query = {
          page,
          perPage: this.pagination.rowsPerPage,
          thread_id,
          start_date,
          end_date,
          countries
        };

        if (this.$route.name === 'scheduled-threads-today') {
          query.start_date = this.$dayjs().startOf('day').format('YYYY-MM-DD HH:ss');
          query.end_date = this.$dayjs().endOf('day').format('YYYY-MM-DD HH:ss');
        }

        this.isLoading = true;
        return this.$store
          .dispatch('schedules/getThreads', query)
          .then((res) => {
            const { total, current_page, last_page } = this.threadsMeta;
            this.pagination.page = current_page;
            this.pagination.totalItems = total;
            this.totalPages = last_page;
            this.isLoading = false;
            return res;
          })
          .catch((err) => {
            this.isLoading = false;
            return err;
          });
      },
      onFilter(filters) {
        this.query = {
          ...this.getSearchQuery(filters),
        };

        this.getThreads(this.query);
      },
      getSearchQuery({ countries }) {
        const query = {};
        query.countries = countries;

        return query;
      },
      onClearFilters() {
        const { sortBy } = this.pagination;
        const nextQuery = { sort: sortBy };

        this.query = nextQuery;
        this.getThreads(nextQuery);
      },
      onChangePage(page) {
        const { query, pagination } = this;
        const { order, sortBy } = pagination;
        const nextQuery = {
          ...query,
          page,
          order,
          sort: sortBy,
        };

        this.query = nextQuery;
        this.getThreads(nextQuery);
      },
      onUpdatePagination({ page, order, sortBy }) {
        const { query, pagination } = this;
        this.pagination.order = order;
        this.pagination.sortBy = isStr(sortBy) ? sortBy : pagination.sortBy;

        const nextQuery = {
          ...query,
          page,
          order,
          sort: isStr(sortBy) ? sortBy : pagination.sortBy,
        };

        this.query = nextQuery;
        this.getThreads(nextQuery);
      },
      selectThread(thread) {
        // nothing to do here
      },
      displayColumnValue(item, columnValue) {
        return extractNestedProp(item, columnValue);
      },
    },
  };
</script>

<style lang="scss" scoped>
@use '../../assets/styles/theme' as *;

[data-view='scheduled-threads'] {
  .v-datatable {
    min-width: $table-min-width;
  }

  .merchant-name {
    font-weight: bold;
  }
}
</style>

<style lang="scss">
[data-view='scheduled-threads'] {
  [data-component="table"] table tbody tr {
    cursor: default;
  }
}
</style>
